import { Box, Button, Divider, Flex, Text } from '@chakra-ui/react';
import {
  PossibleEnrichments,
  SocialNetwork as SocialMediaNetworkName,
  useVendor
} from '@outdoorly/react-chat-sdk';
import { AnimatePresence } from 'framer-motion';
import posthog from 'posthog-js';
import React, { useMemo, useRef, useState } from 'react';
import {
  FacebookLogo,
  InstagramLogo,
  LeftChevron,
  LinkedinLogo,
  LocationIcon,
  PinterestLogo,
  RedditLogo,
  SpotifyLogo,
  TikTokLogo,
  TumblrLogo,
  TwitterLogo,
  VimeoLogo,
  YouTubeLogo
} from '../../icons';
import { Store } from '../../store';
import { tryToBuildImgixUrl } from '../../utils/imgix';
import { RemarkCarousel } from '../Carousel';
import { RemarkDivider } from '../Divider';
import { EmojiAndLinkifiedText } from '../Message/EmojiAndLinkifiedText';
import { MotionBox, MotionFlex } from '../Motion';
import { PoweredByRemark } from '../PoweredByRemark';
import { SkeletonImage } from '../SkeletonImage';
import { ExpertProfileAvatar } from './ExpertProfileAvatar';
import { MatchedExpertPill } from './MatchedExpertPill';
import { RemarkGallery } from './RemarkGallery';
import { WorkExperience } from './WorkExperience';

type ExpertProfileProps = {
  expertProfile: NonNullable<PossibleEnrichments['expert']>['expertProfile'];
  messageSid?: string;
};
const MAX_SOCIAL_MEDIA_LINKS = 4;
const SOCIAL_MEDIA_ICONS = {
  [SocialMediaNetworkName.Facebook]: (
    <FacebookLogo
      fill="scheme.contentInverseSecondary"
      mx="4px"
      _hover={{ fill: 'scheme.contentTertiary' }}
    />
  ),
  [SocialMediaNetworkName.Instagram]: (
    <InstagramLogo
      fill="scheme.contentInverseSecondary"
      mx="4px"
      _hover={{ fill: 'scheme.contentTertiary' }}
    />
  ),
  [SocialMediaNetworkName.LinkedIn]: (
    <LinkedinLogo
      fill="scheme.contentInverseSecondary"
      mx="4px"
      _hover={{ fill: 'scheme.contentTertiary' }}
    />
  ),
  // [SocialMediaNetworkName.personal]: (
  //   <FontAwesomeIcon icon={faGlobe} color="scheme.contentInverseSecondary" width="16px" height="16px" />
  // ),
  [SocialMediaNetworkName.Pinterest]: (
    <PinterestLogo
      fill="scheme.contentInverseSecondary"
      mx="4px"
      _hover={{ fill: 'scheme.contentTertiary' }}
    />
  ),
  [SocialMediaNetworkName.Reddit]: (
    <RedditLogo fill="scheme.contentInverseSecondary" mx="4px" _hover={{ fill: 'scheme.contentTertiary' }} />
  ),
  [SocialMediaNetworkName.Spotify]: (
    <SpotifyLogo fill="scheme.contentInverseSecondary" mx="4px" _hover={{ fill: 'scheme.contentTertiary' }} />
  ),
  [SocialMediaNetworkName.TikTok]: (
    <TikTokLogo fill="scheme.contentInverseSecondary" mx="4px" _hover={{ fill: 'scheme.contentTertiary' }} />
  ),
  [SocialMediaNetworkName.Tumblr]: (
    <TumblrLogo fill="scheme.contentInverseSecondary" mx="4px" _hover={{ fill: 'scheme.contentTertiary' }} />
  ),
  [SocialMediaNetworkName.Twitter]: (
    <TwitterLogo fill="scheme.contentInverseSecondary" mx="4px" _hover={{ fill: 'scheme.contentTertiary' }} />
  ),
  [SocialMediaNetworkName.Vimeo]: (
    <VimeoLogo fill="scheme.contentInverseSecondary" mx="4px" _hover={{ fill: 'scheme.contentTertiary' }} />
  ),
  [SocialMediaNetworkName.YouTube]: (
    <YouTubeLogo fill="scheme.contentInverseSecondary" mx="4px" _hover={{ fill: 'scheme.contentTertiary' }} />
  )
};

export const ExpertProfile: React.FC<ExpertProfileProps> = (props) => {
  const { expertProfile, messageSid } = props;
  const [fullscreen, setFullscreen] = useState<boolean>(false);
  const expertProfileRef = useRef<null | HTMLDivElement>(null);
  const labels = Store.useState((s) => s.labels);
  const { vendor } = useVendor();
  const leftButtonRef = useRef<null | HTMLButtonElement>(null);
  const [galleryMode, setGalleryMode] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState<number>(0);
  const expertMatchedMessage = Store.useState((s) => s.expertMatchedMessage[messageSid ?? ''], [messageSid]);
  const AITransferExpertJoined = Store.useState(
    (s) => s.AITransferExpertJoined[messageSid ?? ''],
    [messageSid]
  );
  const slides = useMemo(() => {
    const allSlides = expertProfile?.gallery
      ?.concat(expertProfile.coverPhoto ? { id: 'cover-photo', url: expertProfile.coverPhoto } : [])
      .map((galleryPhoto, index) => (
        <MotionBox
          key={galleryPhoto.id}
          listStyleType="none"
          animate={{
            height: fullscreen ? '160px' : '96px',
            width: fullscreen ? '160px' : '96x'
          }}
          initial={{
            height: '96px',
            width: '96px'
          }}
          minW="96px"
          mt={fullscreen ? '4px' : 0}
        >
          <SkeletonImage
            src={tryToBuildImgixUrl(galleryPhoto.url, {
              h: 200,
              w: 200,
              auto: 'format,compress',
              crop: 'faces',
              fit: 'crop'
            })}
            imageProps={{
              objectFit: 'cover',
              borderRadius: '10px',
              transform: fullscreen ? 'none' : index % 2 === 0 ? 'rotate(-3deg)' : 'rotate(3deg)',
              p: '4px',
              height: '100%',
              width: '100%',
              sx: {
                WebkitUserDrag: 'none',
                WebkitUserSelect: 'none'
              },
              onClick: () => {
                if (!galleryMode) {
                  posthog.capture('VIEW-EXPERT-GALLERY', {
                    expert_id: expertProfile?.id,
                    expert_name: expertProfile?.publicName
                  });
                  setGalleryMode(true);
                  setGalleryIndex(index);
                }
              },
              role: 'presentation',
              cursor: 'zoom-in',
              filter: 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.2))'
            }}
          />
        </MotionBox>
      ));
    if (expertProfile?.avatar && !fullscreen && !galleryMode) {
      allSlides?.unshift(
        <ExpertProfileAvatar
          fullscreen={fullscreen}
          setFullscreen={setFullscreen}
          expertProfile={expertProfile}
          key="expert profile avatar"
        />
      );
    }
    return allSlides;
  }, [fullscreen, expertProfile, galleryMode]);

  const shouldShowWidePhoto =
    (expertProfile?.coverPhoto && !expertProfile?.gallery.length) ||
    (expertProfile?.gallery?.length === 1 && !expertProfile?.coverPhoto);

  return (
    <Box>
      <MotionBox
        animate={{
          height: fullscreen || galleryMode ? '100%' : 'auto'
        }}
        position={fullscreen || galleryMode ? 'absolute' : 'relative'}
        w="100%"
        ref={expertProfileRef}
        id="expert-ref-id"
        zIndex={fullscreen || galleryMode ? 'modal' : undefined}
        role="row"
        aria-busy={true}
        top={0}
        background="scheme.backgroundPrimary"
        left={0}
      >
        {galleryMode && (
          <RemarkGallery
            gallery={expertProfile?.gallery?.concat(
              expertProfile.coverPhoto ? { id: 'cover-photo', url: expertProfile.coverPhoto } : []
            )}
            galleryIndex={galleryIndex}
            expertProfile={expertProfile}
          />
        )}

        {!galleryMode && (
          <>
            <Box h={fullscreen ? '159px' : '100px'} w="100%" pos={'relative'} mt={fullscreen ? 0 : '4px'}>
              {slides && slides.length > 1 && !shouldShowWidePhoto ? (
                <RemarkCarousel
                  leftButtonRef={leftButtonRef}
                  frameAriaLabel="expert gallery photos"
                  focusable={fullscreen}
                  slidesToShow={fullscreen ? undefined : 3.2}
                  slides={slides ?? []}
                  slideWidth={fullscreen ? '160px' : '104px'}
                  slidesToScroll={1}
                  cellSpacing={8}
                  key={`expert-gallery-photos-${fullscreen}`}
                  hideBullets={true}
                  wrapAround={false}
                  overflow="visible"
                />
              ) : shouldShowWidePhoto ? (
                <Flex align="center" mb="12px">
                  <ExpertProfileAvatar
                    fullscreen={fullscreen}
                    setFullscreen={setFullscreen}
                    expertProfile={expertProfile}
                  />
                  <SkeletonImage
                    src={tryToBuildImgixUrl(
                      expertProfile.coverPhoto ? expertProfile.coverPhoto : expertProfile.gallery[0].url,
                      {
                        w: 400,
                        h: 300,
                        auto: 'compress,format'
                      }
                    )}
                    imageProps={{
                      width: fullscreen ? '100%' : 'calc(100% - 120px)',
                      height: fullscreen ? '159px' : '96px',
                      objectFit: 'cover',
                      borderRadius: fullscreen ? 0 : '6px',
                      role: 'presentation',
                      marginLeft: fullscreen ? 0 : '24px',
                      transform: fullscreen ? 'none' : 'rotate(-2deg)',
                      sx: {
                        WebkitUserDrag: 'none',
                        WebkitUserSelect: 'none'
                      },
                      onClick: () => {
                        if (!fullscreen) {
                          posthog.capture('VIEW-EXPERT-PROFILE', {
                            expert_id: expertProfile?.id,
                            expert_name: expertProfile?.publicName
                          });
                          setFullscreen(true);
                        }
                      },
                      cursor: fullscreen ? 'auto' : 'pointer'
                    }}
                  />
                </Flex>
              ) : (
                <></>
              )}
            </Box>
            {fullscreen && (
              <ExpertProfileAvatar
                fullscreen={fullscreen}
                setFullscreen={setFullscreen}
                expertProfile={expertProfile}
              />
            )}
            <MotionBox
              padding={fullscreen ? '8px' : 0}
              role="gridcell"
              tabIndex={-1}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  posthog.capture('VIEW-EXPERT-PROFILE', {
                    expert_id: expertProfile?.id,
                    expert_name: expertProfile?.publicName
                  });
                  setFullscreen(true);
                } else if (e.key === 'Escape') {
                  posthog.capture('CLOSE-EXPERT-PROFILE', {
                    expert_id: expertProfile?.id,
                    expert_name: expertProfile?.publicName
                  });
                  setFullscreen(false);
                }
              }}
            >
              <MotionFlex justify="space-between" align="center" marginTop={fullscreen ? '60px' : '12px'}>
                <Flex align="center">
                  {expertProfile?.publicName && (
                    <Text
                      variant="heading-medium"
                      color="scheme.contentPrimary"
                      mr="8px"
                      fontWeight={700}
                      fontSize="16px"
                    >
                      {expertProfile?.publicName}
                    </Text>
                  )}
                  {expertProfile?.socialLinks
                    ?.filter((social) => social.link)
                    ?.slice(0, MAX_SOCIAL_MEDIA_LINKS)
                    .map((social) => {
                      if (
                        SOCIAL_MEDIA_ICONS[social.socialMediaNetworkName as keyof typeof SOCIAL_MEDIA_ICONS]
                      ) {
                        return (
                          <Box
                            onClick={() => {
                              posthog.capture('CLICK-EXPERT-SOCIAL', {
                                expert_id: expertProfile?.id,
                                expert_name: expertProfile?.publicName,
                                social_name: social.socialMediaNetworkName,
                                social_link: social.link
                              });
                              if (social.link) window.open(social.link);
                            }}
                            key={social.socialMediaNetworkName}
                          >
                            {
                              SOCIAL_MEDIA_ICONS[
                                social.socialMediaNetworkName as keyof typeof SOCIAL_MEDIA_ICONS
                              ]
                            }
                          </Box>
                        );
                      }
                    })}
                  {/* TODO add personal link back in */}
                </Flex>
                {!fullscreen && (
                  <Text
                    _hover={{ textDecoration: 'underline' }}
                    cursor="pointer"
                    variant="label-small"
                    color="blue.600"
                    onClick={() => {
                      posthog.capture('VIEW-EXPERT-PROFILE', {
                        expert_id: expertProfile?.id,
                        expert_name: expertProfile?.publicName
                      });
                      setFullscreen(true);
                    }}
                  >
                    {labels.viewExpertProfile}
                  </Text>
                )}
              </MotionFlex>
              <Text
                mt="12px"
                mb="4px"
                variant="paragraph-small"
                color="scheme.contentSecondary"
                display={fullscreen ? 'block' : '-webkit-box'}
                sx={{
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 3
                }}
                textOverflow="ellipsis"
                overflow="hidden"
              >
                {expertProfile?.bio}
              </Text>
              <Flex flexWrap="wrap">
                <MatchedExpertPill
                  icon={vendor?.expertise?.icon ?? ''}
                  text={vendor?.expertise?.text ?? ''}
                />
                <MatchedExpertPill iconComponent={<LocationIcon />} text={expertProfile?.location ?? ''} />
              </Flex>
              {fullscreen ? (
                <>
                  <RemarkDivider my="12px" />
                  <WorkExperience qualifications={expertProfile?.qualifications} />
                  <RemarkDivider my="12px" />
                  <PoweredByRemark />
                </>
              ) : (
                <>
                  {AITransferExpertJoined ? (
                    <Box
                      w="100%"
                      p="8px"
                      my="12px"
                      borderRadius="8px"
                      backgroundColor="scheme.purple0"
                      border="1px solid rgba(160, 79, 235, 0.20)"
                      boxShadow="0px 2px 4px 0px rgba(0, 0, 0, 0.06)"
                    >
                      <Text variant="label-medium" color="scheme.purple7" opacity={0.6}>
                        {labels.aiChatTransferInlineMessage}
                      </Text>
                    </Box>
                  ) : (
                    <Divider borderColor="scheme.backgroundSecondary" my="16px" />
                  )}
                  {expertMatchedMessage?.length > 0 && (
                    <Flex w="100%">
                      <Box
                        flexBasis={'80%'}
                        ml={'28px'}
                        p="12px"
                        background={'scheme.backgroundSecondary'}
                        borderRadius="8px"
                        maxWidth={'fit-content'}
                      >
                        <Flex>
                          <Box
                            minW="2px"
                            w="2px"
                            background="scheme.borderPrimary"
                            borderRadius="2px"
                            mr="12px"
                          />
                          <Flex flexDir="column">
                            <Text
                              fontSize="10px"
                              color="scheme.contentTertiary"
                              letterSpacing={'0.1px'}
                              fontWeight={600}
                              mb="3px"
                              lineHeight="13px"
                            >
                              YOUR MESSAGE
                            </Text>
                            <Text
                              variant="paragraph-medium"
                              color={'scheme.contentPrimary'}
                              display="-webkit-box"
                              sx={{
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 2
                              }}
                              overflow="hidden"
                            >
                              <EmojiAndLinkifiedText
                                body={expertMatchedMessage ?? ''}
                                id={'message?.sid ?? '}
                              />
                            </Text>
                          </Flex>
                        </Flex>
                      </Box>
                    </Flex>
                  )}
                </>
              )}
            </MotionBox>
          </>
        )}
        <AnimatePresence>
          {(fullscreen || galleryMode) && (
            <Button
              pos="fixed"
              top="12px"
              left="12px"
              variant="back-button"
              zIndex="modal"
              leftIcon={<LeftChevron fill="scheme.contentInverseSecondary" w="6px" />}
              onClick={() => {
                posthog.capture('CLOSE-EXPERT-PROFILE', {
                  expert_id: expertProfile?.id,
                  expert_name: expertProfile?.publicName
                });
                setFullscreen(false);
                setGalleryMode(false);
              }}
            >
              {labels.backToConversation}
            </Button>
          )}
        </AnimatePresence>
      </MotionBox>
      {(fullscreen || galleryMode) && <Box h="270px" w="100%" />}
    </Box>
  );
};
